import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Smartphone, MapPin, Link2, Plus, Copy, Pencil, Search, ChevronRight, ShieldOff, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";
import { usePreview } from "@/context/PreviewContext";
import { PSectionLabel, PSpinner, useProducerTheme, portalSurface } from "@/components/producer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function StatCard({ icon: Icon, value, label, sub, testId }) {
  return (
    <div className="p-card flex items-center gap-4 p-5" data-testid={testId}>
      <div className="flex h-11 w-11 items-center justify-center rounded-xl p-accent-text" style={{ background: "var(--p-accent-soft)" }}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="font-mono-g text-2xl font-black leading-none">{value}</p>
        <p className="mt-1 text-sm font-semibold">{label}</p>
        <p className="text-xs p-subtle">{sub}</p>
      </div>
    </div>
  );
}

function LocationPicker({ locations, selected, onToggle }) {
  return (
    <div className="space-y-2">
      {locations.map((l) => (
        <label key={l.location_id} data-testid={`loc-pick-${l.location_id}`}
          className="flex cursor-pointer items-center gap-3 rounded-lg p-inset p-3">
          <Checkbox checked={selected.includes(l.location_id)} onCheckedChange={() => onToggle(l.location_id)} />
          <span className="flex items-center gap-2 text-sm font-medium">
            <MapPin className="h-3.5 w-3.5 p-subtle" /> {l.label}{l.city ? ` · ${l.city}` : ""}
          </span>
        </label>
      ))}
    </div>
  );
}

function GenerateDialog({ locations, theme }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const reset = () => { setLabel(""); setPicked([]); setCreated(null); };

  const submit = async () => {
    if (!label.trim()) { toast.error("Name this enrollment link"); return; }
    if (picked.length === 0) { toast.error("Select at least one location"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/producer/flight/enrollments", { label: label.trim(), location_ids: picked });
      setCreated(data);
      qc.invalidateQueries({ queryKey: ["flight-overview"] });
      toast.success("Enrollment link created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create link");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <Button data-testid="generate-enrollment-btn" onClick={() => setOpen(true)} className="p-btn px-4">
        <Plus className="mr-1.5 h-4 w-4" /> Generate Enrollment Link
      </Button>
      <DialogContent className={`goose-producer ${theme === "dark" ? "theme-dark" : ""} ${portalSurface(theme)}`}>
        <DialogHeader><DialogTitle className="font-display text-xl">Generate enrollment link</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Create a private link event staff open to enroll the Goose app on their device, scoped to the chosen locations.</DialogDescription>
        {created ? (
          <div className="space-y-3 py-2" data-testid="enrollment-created">
            <p className="text-sm p-muted">Share this private link with your event staff. They open it on their device to install Goose. You can copy it again anytime from the list.</p>
            <button onClick={() => { navigator.clipboard.writeText(created.url); toast.success("Link copied"); }}
              data-testid="copy-created-link"
              className="flex w-full items-center justify-between gap-2 rounded-lg p-inset p-hover-accent p-3 text-left text-xs p-muted">
              <span className="min-w-0 truncate">{created.url}</span><Copy className="h-4 w-4 shrink-0" />
            </button>
            <Button onClick={() => { setOpen(false); reset(); }} className="p-btn w-full">Done</Button>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <div>
                <Label className="mb-1.5 block text-xs p-muted">Link name</Label>
                <Input data-testid="enrollment-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Neon Friday Staff" />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs p-muted">Locations these devices can access</Label>
                <LocationPicker locations={locations} selected={picked} onToggle={toggle} />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="enrollment-submit-btn" disabled={saving} onClick={submit} className="p-btn w-full">
                {saving ? "Creating…" : "Create link"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditDeviceDialog({ device, locations, theme }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(device.label);
  const [picked, setPicked] = useState(device.location_ids || []);
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const openDialog = () => { setLabel(device.label); setPicked(device.location_ids || []); setOpen(true); };

  const submit = async () => {
    if (picked.length === 0) { toast.error("Select at least one location"); return; }
    setSaving(true);
    try {
      await api.patch(`/producer/flight/devices/${device.device_id}`, { label: label.trim(), location_ids: picked });
      qc.invalidateQueries({ queryKey: ["flight-overview"] });
      toast.success("Device updated");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update device");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? openDialog() : setOpen(false))}>
      <button data-testid={`device-edit-${device.device_id}`} onClick={openDialog}
        className="flex h-8 w-8 items-center justify-center rounded-lg p-hover-accent p-muted" title="Edit device">
        <Pencil className="h-4 w-4" />
      </button>
      <DialogContent className={`goose-producer ${theme === "dark" ? "theme-dark" : ""} ${portalSurface(theme)}`}>
        <DialogHeader><DialogTitle className="font-display text-xl">Edit device</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Rename this device or change which of your locations it can access. Staff do not need to reinstall.</DialogDescription>
        <div className="space-y-4 py-2">
          <div>
            <Label className="mb-1.5 block text-xs p-muted">Device name</Label>
            <Input data-testid="device-label-input" value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs p-muted">Authorized locations</Label>
            <LocationPicker locations={locations} selected={picked} onToggle={toggle} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="device-save-btn" disabled={saving} onClick={submit} className="p-btn w-full">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeviceStatus({ status }) {
  const map = {
    active: { c: "text-emerald-600 bg-emerald-500/10", dot: "bg-emerald-500", t: "Active" },
    inactive: { c: "text-slate-500 bg-slate-500/10", dot: "bg-slate-400", t: "Inactive" },
    expired: { c: "text-amber-600 bg-amber-500/10", dot: "bg-amber-500", t: "Expired" },
    revoked: { c: "text-red-600 bg-red-500/10", dot: "bg-red-500", t: "Revoked" },
  };
  const s = map[status] || map.inactive;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${s.c}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} /> {s.t}
    </span>
  );
}

function LinkUrlDialog({ link, onClose, theme }) {
  return (
    <Dialog open={!!link} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`goose-producer ${theme === "dark" ? "theme-dark" : ""} ${portalSurface(theme)}`}>
        <DialogHeader><DialogTitle className="font-display text-xl">Enrollment link ready</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Copy this private link now — for security it is shown only once and expires in 24 hours.</DialogDescription>
        {link && (
          <div className="space-y-3 py-2" data-testid="link-url-dialog">
            <p className="text-sm p-muted">Copy this link now — for security it's shown only once and expires in 24 hours. If staff lose it, generate a replacement.</p>
            <button onClick={() => { navigator.clipboard.writeText(link.url); toast.success("Link copied"); }}
              data-testid="link-url-copy"
              className="flex w-full items-center justify-between gap-2 rounded-lg p-inset p-hover-accent p-3 text-left text-xs p-muted">
              <span className="min-w-0 truncate">{link.url}</span><Copy className="h-4 w-4 shrink-0" />
            </button>
            <Button onClick={onClose} className="p-btn w-full">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function FlightControlPanel() {
  const { theme } = useProducerTheme();
  const { preview } = usePreview();
  const readOnly = !!preview;
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [newLink, setNewLink] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["flight-overview"],
    queryFn: async () => (await api.get("/producer/flight")).data,
  });

  const replaceLink = async (id) => {
    try {
      const { data: d } = await api.post(`/producer/flight/enrollments/${id}/replace`);
      qc.invalidateQueries({ queryKey: ["flight-overview"] });
      setNewLink(d);
      toast.success("Replacement link created");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not replace"); }
  };

  const revokeDevice = async (id) => {
    try {
      await api.post(`/producer/flight/devices/${id}/revoke`);
      qc.invalidateQueries({ queryKey: ["flight-overview"] });
      toast.success("Device revoked");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not revoke"); }
  };
  const revokeLink = async (id) => {
    try {
      await api.post(`/producer/flight/enrollments/${id}/revoke`);
      qc.invalidateQueries({ queryKey: ["flight-overview"] });
      toast.success("Enrollment link revoked");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not revoke"); }
  };

  const devices = useMemo(() => {
    const all = data?.devices || [];
    const query = q.trim().toLowerCase();
    return all.filter((d) => {
      if (statusFilter !== "all" && d.status !== statusFilter) return false;
      if (!query) return true;
      return d.label.toLowerCase().includes(query) || (d.model || "").toLowerCase().includes(query);
    });
  }, [data, q, statusFilter]);

  if (isLoading) return <PSpinner />;
  const { stats, enrollment_links: links, locations } = data;

  return (
    <div data-testid="flight-control-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <PSectionLabel>Account</PSectionLabel>
          <h1 className="font-display mt-1 text-3xl font-bold">Flight Control</h1>
          <p className="mt-1 text-sm p-muted">Manage devices for event-night access. Staff use the Goose app to look up badges and see introductions.</p>
        </div>
        {!readOnly && <GenerateDialog locations={locations} theme={theme} />}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={Smartphone} value={stats.authorized_devices} label="Authorized Devices" sub="Can access assigned locations" testId="stat-devices" />
        <StatCard icon={MapPin} value={stats.locations_assigned} label="Locations" sub="Assigned to devices" testId="stat-locations" />
        <StatCard icon={Link2} value={stats.active_enrollment_links} label="Active Enrollment Link" sub="Share with event staff" testId="stat-links" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Enrollment links */}
        <div className="p-card min-w-0 p-6 lg:col-span-2" data-testid="enrollment-links-card">
          <h3 className="font-display text-lg font-semibold">Enrollment Links</h3>
          <p className="text-sm p-muted">Create a private link for event staff to enroll the Goose app on their device.</p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wider p-subtle">
                  <th className="pb-2 pr-3">Name</th><th className="pb-2 pr-3">Locations</th>
                  <th className="pb-2 pr-3">Status</th><th className="pb-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {links.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-sm p-subtle">No enrollment links yet.</td></tr>
                )}
                {links.map((l) => (
                  <tr key={l.enrollment_id} data-testid={`link-row-${l.enrollment_id}`} className="border-t p-border-c">
                    <td className="py-3 pr-3">
                      <p className="font-semibold">{l.label}</p>
                      <p className="text-xs p-subtle">
                        {l.status === "active" && l.expires_at ? `Expires ${new Date(l.expires_at).toLocaleString()}` : null}
                      </p>
                    </td>
                    <td className="py-3 pr-3 p-muted">{l.location_labels.join(", ")}</td>
                    <td className="py-3 pr-3"><DeviceStatus status={l.status} /></td>
                    <td className="py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {readOnly ? <span className="text-xs p-subtle">—</span> : (<>
                        <button data-testid={`link-replace-${l.enrollment_id}`} onClick={() => replaceLink(l.enrollment_id)}
                          className="p-btn-ghost px-3 py-1.5 text-xs"><RefreshCw className="mr-1 inline h-3.5 w-3.5" />Replace</button>
                        {l.status === "active" && (
                          <button data-testid={`link-revoke-${l.enrollment_id}`} onClick={() => revokeLink(l.enrollment_id)}
                            className="rounded-full border border-red-500/40 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-500/10">Revoke</button>
                        )}
                        </>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Locations */}
        <div className="p-card p-6" data-testid="locations-card">
          <h3 className="font-display text-lg font-semibold">Locations</h3>
          <p className="text-sm p-muted">Which locations devices can access.</p>
          <div className="mt-4 space-y-2">
            {locations.map((l) => (
              <div key={l.location_id} data-testid={`loc-row-${l.location_id}`} className="flex items-center justify-between p-inset p-3">
                <span className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 p-subtle" />
                  <span><span className="font-semibold">{l.label}</span>{l.city ? <span className="block text-xs p-subtle">{l.city}</span> : null}</span>
                </span>
                <span className="flex items-center gap-1 text-sm p-muted">{l.devices_assigned} {l.devices_assigned === 1 ? "device" : "devices"} <ChevronRight className="h-4 w-4 p-subtle" /></span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Authorized devices */}
      <div className="p-card mt-6 min-w-0 p-6" data-testid="devices-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-lg font-semibold">Authorized Devices ({(data.devices || []).length})</h3>
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 p-subtle" />
              <Input data-testid="device-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by device name…" className="h-9 w-full pl-9 sm:w-56" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="device-status-filter" className="h-9 w-36"><SelectValue /></SelectTrigger>
              <SelectContent className={portalSurface(theme)}>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="revoked">Revoked</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="devices-table">
            <thead>
              <tr className="text-xs font-semibold uppercase tracking-wider p-subtle">
                <th className="pb-2 pr-3">Device Name</th><th className="pb-2 pr-3">Locations</th>
                <th className="pb-2 pr-3">Last Used</th><th className="pb-2 pr-3">Status</th><th className="pb-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-sm p-subtle">No devices match.</td></tr>
              )}
              {devices.map((d) => (
                <tr key={d.device_id} data-testid={`device-row-${d.device_id}`} className="border-t p-border-c align-middle">
                  <td className="py-3 pr-3">
                    <p className="flex items-center gap-2 font-semibold"><Smartphone className="h-4 w-4 p-subtle" />{d.label}</p>
                    {d.model && <p className="ml-6 text-xs p-subtle">{d.model}</p>}
                  </td>
                  <td className="py-3 pr-3 p-muted">{d.location_labels.join(", ")}</td>
                  <td className="py-3 pr-3 p-muted">{d.last_seen_at ? new Date(d.last_seen_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "—"}</td>
                  <td className="py-3 pr-3"><DeviceStatus status={d.status} /></td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {readOnly ? <span className="text-xs p-subtle">—</span> : (<>
                      {d.status !== "revoked" && <EditDeviceDialog device={d} locations={locations} theme={theme} />}
                      {d.status !== "revoked" && (
                        <button data-testid={`device-revoke-${d.device_id}`} onClick={() => revokeDevice(d.device_id)}
                          className="rounded-full border border-red-500/40 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-500/10">
                          <ShieldOff className="mr-1 inline h-3.5 w-3.5" />Revoke</button>
                      )}
                      </>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <LinkUrlDialog link={newLink} onClose={() => setNewLink(null)} theme={theme} />
    </div>
  );
}
